import { Button, Icon } from '@zoom/components';
import React, { useLayoutEffect, useRef } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

import { SIDEBAR_UPDATE_TIMEOUT, useSidebar } from '../../../components/layout/Sidebar';
import { Adjustment } from '../../../graphql/components';
import { AdjustmentTileContent } from './AdjustmentTileContent';

interface AdjustmentTileProps extends WithTranslation {
  adjustment: Adjustment;
  handleDelete: (adjustment: Adjustment) => void;
  handleEdit: (adjustment: Adjustment) => void;
  isOpen: boolean;
  isPreOpen: boolean;
  timezone: string;
}

export const AdjustmentTileComponent: React.FC<AdjustmentTileProps> = ({
  adjustment,
  handleDelete,
  handleEdit,
  isOpen,
  isPreOpen,
  t,
  timezone,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const { handleScrollbarUpdate, handleScrollPosition, scrollbarContainerRef } = useSidebar();

  useLayoutEffect(() => {
    if ((isOpen || isPreOpen) && ref?.current && scrollbarContainerRef) {
      // Need to wait until opening animation is finished
      setTimeout(() => {
        if (ref?.current) {
          const tileBottomPosition = ref.current.getBoundingClientRect().bottom;
          const windowHeight = window.innerHeight;
          const scrollbarCurrentScrollPosition = scrollbarContainerRef.scrollTop;

          /**
           * Update scroll position of scrollbar container if some part of tile is not visible
           * (bottom of it is below window's viewport)
           */
          if (windowHeight < tileBottomPosition) {
            handleScrollPosition(scrollbarCurrentScrollPosition + (tileBottomPosition - windowHeight));
          }
        }
      }, SIDEBAR_UPDATE_TIMEOUT);
    }

    if (!isOpen) {
      handleScrollbarUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isPreOpen, ref, scrollbarContainerRef]);

  const onEditHandler = () => {
    handleEdit(adjustment);
    handleScrollPosition(); // Scroll to top to enter edit form
  };

  const onDeleteHandler = () => {
    handleDelete(adjustment);
  };

  return (
    <div className="adjustment-item" ref={ref} data-test-id="adjustment">
      <AdjustmentTileContent adjustment={adjustment} timezone={timezone} />
      <div>
        <Button data-test-id="adjustment:edit" kind="link" onClick={onEditHandler}>
          <Icon kind="pencil" size={14} color="#239ed8" />
          <span>{t('layout.action.edit')}</span>
        </Button>
        <Button data-test-id="adjustment:delete" kind="link" onClick={onDeleteHandler}>
          <Icon kind="bin" size={14} color="red" />
          <span>{t('layout.action.delete')}</span>
        </Button>
      </div>
    </div>
  );
};

export const AdjustmentTile = withTranslation()(AdjustmentTileComponent);
